import {ReactNode, Ref, useCallback, useEffect, useRef, useState} from 'react';
import styles from './HorizontalTab.module.scss';
import HorizontalTabItem from './HorizontalTabItem';
import HorizontalTabScrollGradient from './HorizontalTabScrollGradient';
import FullBg from '../../commonSub/component/FullBg';
import MaxWidth, {IMaxWith} from '../../commonSub/component/MaxWidth';
import {setRefs} from '../../commonSub/function';
import useOnScrollAndResize from '../hook/useOnScrollAndResize';
import {DEFAULT_TAB_HEIGHT_PX} from '../scss/util/variable';
import {TabItemVariant} from '../type';
import {getIsUnderDiffThreshold} from '../util/number';

interface IProps<T, K> extends IMaxWith {
  tabList: T[];
  currentTab: string | number;
  onClickTab: (tab: T) => void;
  beforeTabItemElement?: (tab: T) => ReactNode;
  height?: string;
  keyName: K;
  textName: keyof T;
  isFixed?: boolean;
  variant?: TabItemVariant;
  needTextTranslation?: boolean;
  containerRef?: Ref<HTMLUListElement>;
  needScrollGradient?: boolean;
}

export default function HorizontalTab<
  K extends string,
  T extends {[key in K]: string | number},
>({
  tabList,
  currentTab,
  onClickTab,
  beforeTabItemElement,
  height = DEFAULT_TAB_HEIGHT_PX,
  keyName,
  textName,
  isFixed = false,
  variant = 'whiteCircleBg',
  needTextTranslation,
  containerRef: tabRef,
  needScrollGradient = false,
  hasMdWidth,
  hasLgWidth,
}: IProps<T, K>) {
  const containerRef = useRef<HTMLUListElement>(null);
  const [selectedWidthAndLeft, setSelectedWidthAndLeft] = useState<{
    width: number;
    left: number;
  }>();
  const {
    bg,
    activeTextClassName,
    textClassName,
    tabClassName,
    hasActiveEl,
    isOnlyDarkMode,
  } = STYLE_MAP[variant];

  const [isScroll, setIsScroll] = useState(false);
  const [scrollState, setScrollState] = useState<ScrollState>('FIRST');

  const onResize = useCallback((isDisableScrollTo?: boolean) => {
    const container = containerRef.current;
    if (!container) return;
    const center = container.clientWidth / 2;
    const tabItem = container.querySelector<HTMLElement>(
      SELECTED_ITEM_SELECTOR
    );
    if (tabItem) {
      const scrollLeft = container.scrollLeft;
      const containerLeft = container.getBoundingClientRect().left;
      const itemRect = tabItem.getBoundingClientRect();
      const itemLeft = Math.round(itemRect.left);
      const offsetLeft = Math.round(itemLeft - containerLeft + scrollLeft);
      const offsetWidth = Math.round(itemRect.width);

      const isScrollFirst = getIsUnderDiffThreshold({
        value: scrollLeft,
        target: 0,
        threshold: SCROLL_DIFF_THRESHOLD,
      });
      const isScrollLast = getIsUnderDiffThreshold({
        value: scrollLeft + container.clientWidth,
        target: container.scrollWidth,
        threshold: SCROLL_DIFF_THRESHOLD,
      });

      if (isScrollFirst) {
        setScrollState('FIRST');
      } else if (isScrollLast) {
        setScrollState('LAST');
      } else {
        setScrollState('MIDDLE');
      }

      if (container.scrollTo && !isDisableScrollTo) {
        container.scrollTo({
          left: offsetLeft + offsetWidth / 2 - center,
          top: 0,
          behavior: 'smooth',
        });
      }

      // 새로 변경한 코드(아래 주석처리)가 문제가 있어 기존 코드로 복원
      // TODO: 문제점을 찾으면 주석처리한 코드로 변경하자.
      const lastItem = container.querySelector(
        `.${styles.lastItem}`
      ) as HTMLLIElement;
      setIsScroll(
        lastItem.offsetLeft + lastItem.offsetWidth > container.offsetWidth
      );
      // setIsScroll(container.scrollWidth > container.clientWidth);
      setSelectedWidthAndLeft({
        width: offsetWidth,
        left: offsetLeft,
      });
    }
  }, []);
  const onScroll = useCallback(() => onResize(true), [onResize]);

  useEffect(() => {
    // 바로 호출하면 마운트 되고 나서 최초에 스크롤이 안된다.
    requestAnimationFrame(() => onResize());
  }, [currentTab, variant, tabList, onResize]);

  useOnScrollAndResize({
    scrollRef: containerRef,
    onResize,
    onScroll,
    resizeDebounceTime: 200,
  });

  useEffect(() => {
    const container = containerRef.current;
    if (container && isScroll) {
      let isClicked = false;
      const startPosition = {
        container: 0,
        mouse: 0,
      };
      const containerEl = container as HTMLUListElement;
      const mousedown = function (e: MouseEvent) {
        isClicked = true;
        startPosition.mouse = e.clientX;
        startPosition.container = containerEl.scrollLeft;
      };
      const mousemove = function (e: MouseEvent) {
        if (isClicked) {
          const delta = startPosition.mouse - e.clientX;
          containerEl.scrollLeft = startPosition.container + delta;
        }
      };
      const mouseup = function () {
        isClicked = false;
      };
      const mouseleave = function () {
        isClicked = false;
      };
      container.addEventListener('mousedown', mousedown);
      container.addEventListener('mousemove', mousemove);
      container.addEventListener('mouseup', mouseup);
      container.addEventListener('mouseleave', mouseleave);

      return () => {
        container.removeEventListener('mousedown', mousedown);
        container.removeEventListener('mousemove', mousemove);
        container.removeEventListener('mouseup', mouseup);
        container.removeEventListener('mouseleave', mouseleave);
      };
    }
  }, [isScroll]);

  const showLeftGradient =
    needScrollGradient && isScroll && scrollState !== 'FIRST';
  const showRightGradient =
    needScrollGradient && isScroll && scrollState !== 'LAST';

  return (
    <>
      <MaxWidth
        className={cn('relative z-navigationBarMinus1', isFixed && '!fixed')}
        style={{height}}
        hasMdWidth={hasMdWidth}
        hasLgWidth={hasLgWidth}
      >
        {showLeftGradient && (
          <HorizontalTabScrollGradient height={height} isLeft />
        )}
        <FullBg bgClassName={bg} height={height} />
        <div className="w-full h-full relative">
          <ul
            className={cn(
              'left-0 list-none absolute top-0 z-navigationBar',
              'before:h-full before:min-w-[15px] after:h-full after:min-w-[15px]',
              'px-0 my-0',
              'flex-center w-full',
              'no-scrollbar',
              tabClassName,
              styles.tabListWrapper,
              isScroll
                ? cn('overflow-x-auto', styles.isScroll)
                : 'overflow-x-hidden'
            )}
            ref={ref => setRefs(ref, containerRef, tabRef)}
            style={{height}}
          >
            {tabList.map((item: T, index) => (
              <HorizontalTabItem
                key={item[keyName]}
                item={item}
                isSelected={currentTab === item[keyName]}
                onClickTab={onClickTab}
                textName={textName}
                textClassName={textClassName}
                activeTextClassName={activeTextClassName || textClassName}
                className={cn(isScroll ? 'flex-none' : 'flex-1 flex-shrink-0', {
                  [styles.firstItem]: index === 0,
                  [styles.lastItem]: index === tabList.length - 1,
                  [styles.activeItem]: currentTab === item[keyName],
                })}
                needTextTranslation={needTextTranslation}
                beforeElement={beforeTabItemElement}
              />
            ))}

            {hasActiveEl && (
              <li
                className={cn(
                  'px-12 rounded-[16px] h-30 left-0 absolute z-navigationBarMinus1',
                  styles.selected,
                  isOnlyDarkMode ? 'bg-white' : 'bg-white light:bg-black',
                  selectedWidthAndLeft?.left !== undefined
                    ? 'opacity-100'
                    : 'opacity-0'
                )}
                style={{
                  transform: `translateX(${selectedWidthAndLeft?.left || 0}px`,
                }}
              >
                <div
                  style={{
                    width: `${selectedWidthAndLeft?.width || 0}px`,
                  }}
                ></div>
              </li>
            )}
          </ul>
        </div>
        {showRightGradient && (
          <HorizontalTabScrollGradient height={height} isLeft={false} />
        )}
      </MaxWidth>
      {isFixed && <div style={{height}} />}
    </>
  );
}

// TODO: transparent만 쓴다면 TabItemVariant는 지워도 될 것 같다.
const STYLE_MAP: {
  [key in TabItemVariant]: {
    bg: string;
    activeTextClassName?: string;
    textClassName: string;
    tabClassName: string;
    hasActiveEl?: boolean;
    isOnlyDarkMode?: boolean;
  };
} = {
  onlyWhiteUnderlineV4: {
    bg: 'bg-transparent',
    activeTextClassName:
      's14-bold-white underline underline-offset-4 decoration-2',
    textClassName: 's14-bold-white opacity-50',
    tabClassName: cn(styles.onlyWhiteUnderlineV4),
  },
  transparentWhite: {
    bg: 'bg-transparent',
    activeTextClassName: 's16-bold-white',
    textClassName: 's16-bold-grey05',
    tabClassName: styles.transparentWhite,
  },
  whiteCircleBg: {
    bg: 'bg-transparent',
    activeTextClassName: 's14-bold-black light:text-white',
    textClassName: 's14-bold-white light:text-black',
    tabClassName: cn(
      'justify-center w-full',
      'before:min-w-[30px] after:min-w-[30px]',
      styles.whiteCircleBg
    ),
    hasActiveEl: true,
  },
  whiteCircleBgFourOrMoreItems: {
    bg: 'bg-transparent',
    activeTextClassName: 's14-bold-black light:text-white',
    textClassName: 's14-bold-white light:text-black',
    tabClassName: cn(
      'justify-center w-full',
      'before:min-w-[10px] after:min-w-[10px]',
      styles.whiteCircleBg,
      styles.fourOrMore
    ),
    hasActiveEl: true,
  },
  underlineTransparent: {
    bg: 'bg-transparent',
    activeTextClassName: 's14-bold-white light:text-black leading-17',
    textClassName: 's14-bold-grey06 leading-17',
    tabClassName: cn(
      'justify-center w-full gap-x-40',
      'before:min-w-0 after:min-w-0',
      styles.underlineTransparent
    ),
    hasActiveEl: false,
  },
  transparentWhiteSmall: {
    bg: 'bg-transparent',
    activeTextClassName: 's14-bold-white light:text-black',
    textClassName: 's14-bold-grey05',
    tabClassName: cn(
      'justify-center w-full gap-x-24',
      'before:hidden after:hidden',
      '!px-18',
      styles.transparentWhiteSmall
    ),
    hasActiveEl: false,
  },
  whiteUnderline: {
    bg: 'bg-transparent',
    activeTextClassName: 's14-bold-white',
    textClassName: 's14-bold-white opacity-40',
    tabClassName: cn(styles.whiteUnderline, 'before:hidden after:hidden'),
  },
};

const SELECTED_ITEM_SELECTOR = 'li[data-is-selected="true"]';

const SCROLL_DIFF_THRESHOLD = 1;
type ScrollState = 'FIRST' | 'LAST' | 'MIDDLE';
